import { useState, useEffect } from 'react';
import type { Ward } from '../App';
import './Scenarios.css';

interface ScenariosProps {
  councilId: string;
  apiBase: string;
}

interface ScenarioResult {
  ward_id: string;
  ward_name: string;
  baseline_seats: number;
  push_seats: number;
  defensive_seats: number;
  surge_seats: number;
}

function Scenarios({ councilId, apiBase }: ScenariosProps) {
  const [wards, setWards] = useState<Ward[]>([]);
  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Scenario parameters
  const [pushMultiplier, setPushMultiplier] = useState(1.5);
  const [defensiveMultiplier, setDefensiveMultiplier] = useState(0.8);
  const [surgeMultiplier, setSurgeMultiplier] = useState(2.0);

  useEffect(() => {
    fetchWards();
  }, [councilId]);

  useEffect(() => {
    if (wards.length > 0) {
      calculateScenarios();
    }
  }, [wards, pushMultiplier, defensiveMultiplier, surgeMultiplier]);

  const fetchWards = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/councils/${councilId}/wards`);
      const data = await res.json();
      setWards(data);
    } catch (err) {
      console.error('Failed to fetch wards:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateScenarios = () => {
    // Simple expected seats calculation based on FRAI and multipliers
    const scenarioResults: ScenarioResult[] = wards.map((ward) => {
      const baseFRAI = ward.green_agreement_current ?? 0.5;
      
      // Baseline: Current FRAI * seats
      const baselineSeats = baseFRAI * ward.seats;

      // Push: Apply multiplier with diminishing returns
      const pushFRAI = Math.min(1, baseFRAI * Math.sqrt(pushMultiplier));
      const pushSeats = pushFRAI * ward.seats;

      // Defensive: Reduce effort
      const defensiveFRAI = baseFRAI * defensiveMultiplier;
      const defensiveSeats = defensiveFRAI * ward.seats;

      // Surge: Target only high-priority wards heavily
      const surgeMultiplierAdjusted = ward.status === 'priority' ? surgeMultiplier : 1.0;
      const surgeFRAI = Math.min(1, baseFRAI * Math.sqrt(surgeMultiplierAdjusted));
      const surgeSeats = surgeFRAI * ward.seats;

      return {
        ward_id: ward.id,
        ward_name: ward.name,
        baseline_seats: baselineSeats,
        push_seats: pushSeats,
        defensive_seats: defensiveSeats,
        surge_seats: surgeSeats,
      };
    });

    setResults(scenarioResults);
  };

  const totalSeats = {
    baseline: results.reduce((sum, r) => sum + r.baseline_seats, 0),
    push: results.reduce((sum, r) => sum + r.push_seats, 0),
    defensive: results.reduce((sum, r) => sum + r.defensive_seats, 0),
    surge: results.reduce((sum, r) => sum + r.surge_seats, 0),
  };

  if (loading) {
    return (
      <div className="scenarios-loading">
        <div className="spinner"></div>
        <p>Loading scenario sandbox...</p>
      </div>
    );
  }

  return (
    <div className="scenarios">
      <div className="scenarios-header">
        <h2>Scenario Sandbox</h2>
        <p>
          Test different resource allocation strategies. Adjust multipliers to see how
          diminishing returns affect expected seat counts.
        </p>
      </div>

      <div className="scenarios-layout">
        {/* Left: Controls */}
        <div className="scenario-controls">
          <h3>Strategy Settings</h3>

          <div className="control-item">
            <label>
              Push Greens: <strong>{pushMultiplier.toFixed(1)}×</strong>
            </label>
            <input
              type="range"
              min="1.0"
              max="3.0"
              step="0.1"
              value={pushMultiplier}
              onChange={(e) => setPushMultiplier(parseFloat(e.target.value))}
            />
            <small>Applies to 'Push' column</small>
          </div>

          <div className="control-item">
            <label>
              Defensive: <strong>{defensiveMultiplier.toFixed(1)}×</strong>
            </label>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={defensiveMultiplier}
              onChange={(e) => setDefensiveMultiplier(parseFloat(e.target.value))}
            />
            <small>Reduce effort, hold what we have</small>
          </div>

          <div className="control-item">
            <label>
              Surge Multiplier: <strong>{surgeMultiplier.toFixed(1)}×</strong>
            </label>
            <input
              type="range"
              min="1.0"
              max="5.0"
              step="0.5"
              value={surgeMultiplier}
              onChange={(e) => setSurgeMultiplier(parseFloat(e.target.value))}
            />
            <small>Intense focus on priority wards</small>
          </div>

          <div className="control-note">
            <p>
              💡 <strong>Note:</strong> These are simplified projections.
              Real campaigns require detailed ward-level planning.
            </p>
          </div>
        </div>

        {/* Right: Results Table */}
        <div className="scenario-results">
          <table className="scenario-table">
            <thead>
              <tr>
                <th>Ward</th>
                <th>Baseline</th>
                <th>Push Greens</th>
                <th>Defensive</th>
                <th>Targeted Surge</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.ward_id}>
                  <td className="ward-name">{result.ward_name}</td>
                  <td>{result.baseline_seats.toFixed(2)}</td>
                  <td className="col-push">{result.push_seats.toFixed(2)}</td>
                  <td className="col-defensive">{result.defensive_seats.toFixed(2)}</td>
                  <td className="col-surge">{result.surge_seats.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td>TOTAL SEATS</td>
                <td><strong>{totalSeats.baseline.toFixed(1)}</strong></td>
                <td className="col-push"><strong>{totalSeats.push.toFixed(1)}</strong></td>
                <td className="col-defensive"><strong>{totalSeats.defensive.toFixed(1)}</strong></td>
                <td className="col-surge"><strong>{totalSeats.surge.toFixed(1)}</strong></td>
              </tr>
            </tfoot>
          </table>

          <div className="scenario-footnote">
            * Values represent <strong>Expected Seats</strong> (e.g., 1.1 = 1 seat
            guaranteed + 10% probability of a 2nd seat).
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="scenario-insights">
        <div className="insight-card">
          <h4>📊 Best Strategy</h4>
          <p>
            {totalSeats.surge > totalSeats.push && totalSeats.surge > totalSeats.baseline
              ? `Targeted Surge yields ${(totalSeats.surge - totalSeats.baseline).toFixed(1)} extra seats (${((totalSeats.surge - totalSeats.baseline) / totalSeats.baseline * 100).toFixed(1)}% gain)`
              : totalSeats.push > totalSeats.baseline
              ? `Push Greens yields ${(totalSeats.push - totalSeats.baseline).toFixed(1)} extra seats (${((totalSeats.push - totalSeats.baseline) / totalSeats.baseline * 100).toFixed(1)}% gain)`
              : 'Current baseline is already near-optimal'}
          </p>
        </div>

        <div className="insight-card">
          <h4>⚠️ Diminishing Returns</h4>
          <p>
            Doubling resources does NOT double seats due to saturation.
            Focus on high sweet-spot wards for maximum ROI.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Scenarios;
